import {
  AimOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
} from '@ant-design/icons'
import { Tree } from 'antd'
import classNames from 'classnames'
import type RcTree from 'rc-tree'
import {
  forwardRef,
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import TreeEntityCreationModal from '@/components/EntityCreationModals/TreeEntityCreationModal/TreeEntityCreationModal'
import type { TreeEntityCreatedResult } from '@/components/EntityCreationModals/TreeEntityCreationModal/useTreeEntityCreationForm'
import HighlightText from '@/components/Filters/HighlightText/HighlightText'
import { useScrollEdges } from '@/hooks/ui/useScrollEdges'
import type {
  AntdTreeType,
  CategoryInterface,
  NomenclatureGroupInterface,
} from '@/types'
import handleSelectTreeNode from '@/utils/tree/selection/handleSelectTreeNode'
import {
  collectExpandedBranchKeys,
  filterAntdTree,
  getExpandedKeysForValue,
} from '@/utils/tree/selection/treeExpandedKeys'

import cn from './HierarchyTreeColumn.module.scss'

type HierarchyTreeColumnPropsType = {
  treeType: 'nomenclature-groups' | 'categories'
  title: string
  tree: NomenclatureGroupInterface[] | CategoryInterface[]
  antdTree: AntdTreeType[]
  selectedId?: string
  required?: boolean
  errorMessage?: string
  onSelect: (id: string, path: string) => void
  onEntityCreated: () => void
  setExternalTree:
    | ((tree: CategoryInterface[]) => void)
    | ((tree: NomenclatureGroupInterface[]) => void)
  isOrganisationSelected?: boolean
}

const TREE_HEIGHT_DEFAULT = 420

type ColumnTreePropsType = {
  treeData: AntdTreeType[]
  selectedId?: string
  expandedKeys: string[]
  query: string
  height: number
  onExpand: (keys: string[]) => void
  onSelectNode: (id: string) => void
}

const ColumnTree = memo(
  forwardRef<RcTree, ColumnTreePropsType>(function ColumnTree(
    {
      treeData,
      selectedId,
      expandedKeys,
      query,
      height,
      onExpand,
      onSelectNode,
    },
    ref
  ) {
    const titleRender = useCallback(
      (node: AntdTreeType) => {
        const label = String(node.filterProp ?? node.title ?? '')
        return (
          <span className={node.disabled ? cn.nodeDisabled : cn.nodeLabel}>
            <HighlightText text={label} query={query} />
          </span>
        )
      },
      [query]
    )

    return (
      <Tree
        ref={ref}
        treeData={treeData}
        height={height}
        selectedKeys={selectedId ? [selectedId] : []}
        expandedKeys={expandedKeys}
        onExpand={(keys) => onExpand(keys as string[])}
        onSelect={(keys) => {
          onSelectNode(keys[0] ? String(keys[0]) : '')
        }}
        titleRender={titleRender}
      />
    )
  })
)

function HierarchyTreeColumn(props: HierarchyTreeColumnPropsType) {
  const {
    treeType,
    title,
    tree,
    antdTree,
    selectedId,
    required,
    errorMessage,
    onSelect,
    onEntityCreated,
    setExternalTree,
    isOrganisationSelected = false,
  } = props

  const [searchValue, setSearchValue] = useState('')
  const deferredSearchValue = useDeferredValue(searchValue)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<string[]>(() =>
    getExpandedKeysForValue(tree, selectedId)
  )

  const treeWrapperRef = useRef<HTMLDivElement>(null)
  const treeRef = useRef<RcTree>(null)
  const [measuredTreeHeight, setMeasuredTreeHeight] = useState<number | null>(
    null
  )
  const [treeListElement, setTreeListElement] = useState<HTMLElement | null>(
    null
  )
  const { hasContentAbove, hasContentBelow } = useScrollEdges(treeListElement)

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const element = treeWrapperRef.current
    if (!element) return

    const updateHeight = () => {
      const height = element.clientHeight
      if (height > 0) setMeasuredTreeHeight(height)
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [])

  const treeHeight = measuredTreeHeight ?? TREE_HEIGHT_DEFAULT

  useEffect(() => {
    const pathKeys = getExpandedKeysForValue(tree, selectedId)
    if (!pathKeys.length) return
    setExpandedKeys((prev) => Array.from(new Set([...prev, ...pathKeys])))
  }, [tree, selectedId])

  const selectedPath = useMemo(
    () => (selectedId ? handleSelectTreeNode(tree, selectedId) || '' : ''),
    [tree, selectedId]
  )

  const normalizedQuery = deferredSearchValue.trim().toLowerCase()
  const isSearchActive = normalizedQuery.length > 0

  const displayedTreeData = useMemo(
    () =>
      isSearchActive ? filterAntdTree(antdTree, normalizedQuery) : antdTree,
    [antdTree, isSearchActive, normalizedQuery]
  )

  useLayoutEffect(() => {
    setTreeListElement(
      treeWrapperRef.current?.querySelector<HTMLElement>(
        '.ant-tree-list-holder'
      ) ?? null
    )
  }, [displayedTreeData, treeHeight])

  const displayedExpandedKeys = useMemo(
    () =>
      isSearchActive
        ? collectExpandedBranchKeys(displayedTreeData)
        : expandedKeys,
    [displayedTreeData, expandedKeys, isSearchActive]
  )

  const handleExpand = useCallback(
    (keys: string[]) => setExpandedKeys(keys),
    []
  )

  const handleSelectNode = useCallback(
    (id: string) => {
      const path = handleSelectTreeNode(tree, id) || ''
      onSelect(id, path)
    },
    [tree, onSelect]
  )

  const handleCreated = useCallback(
    (created?: TreeEntityCreatedResult) => {
      onEntityCreated()
      if (created?.id) onSelect(created.id, created.description)
    },
    [onEntityCreated, onSelect]
  )

  const handleClearSelection = useCallback(() => onSelect('', ''), [onSelect])

  const handleShowSelected = useCallback(() => {
    if (!selectedId) return
    setSearchValue('')
    setExpandedKeys((prev) =>
      Array.from(
        new Set([...prev, ...getExpandedKeysForValue(tree, selectedId)])
      )
    )
    requestAnimationFrame(() => {
      treeRef.current?.scrollTo({ key: selectedId, align: 'auto' })
    })
  }, [selectedId, tree])

  const createLabel = `Добавить ${treeType === 'categories' ? 'категорию' : 'группу'}`

  return (
    <div
      className={classNames(
        cn.column,
        isOrganisationSelected && cn.accentSelected
      )}
    >
      <div
        className={classNames(cn.selectionBlock, errorMessage && cn.invalid)}
      >
        <div className={cn.header}>
          <span className={cn.title}>
            {title}
            {required && (
              <span className={cn.requiredMark} aria-hidden>
                *
              </span>
            )}
          </span>
          <AtomButton
            variant="secondary"
            shape="circle"
            icon={<PlusOutlined />}
            aria-label={createLabel}
            title={createLabel}
            className={cn.addButton}
            onClick={() => setIsCreateModalOpen(true)}
          />
        </div>

        <AtomSearchInput
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Поиск..."
          allowClear
          isOrganisationSelected={isOrganisationSelected}
        />
        <div
          className={classNames(
            cn.treeWrapper,
            hasContentAbove && cn.fadeTop,
            hasContentBelow && cn.fadeBottom
          )}
          ref={treeWrapperRef}
        >
          <ColumnTree
            ref={treeRef}
            treeData={displayedTreeData}
            selectedId={selectedId}
            expandedKeys={displayedExpandedKeys}
            query={deferredSearchValue}
            height={treeHeight}
            onExpand={handleExpand}
            onSelectNode={handleSelectNode}
          />
          {isSearchActive && !displayedTreeData.length && (
            <span className={cn.searchEmpty}>Ничего не найдено</span>
          )}
        </div>
        {selectedId && (
          <div className={cn.selectedPath}>
            <span className={cn.selectedPathText}>
              <span>Выбрано:</span>{' '}
              <span className={cn.selectedPathValue}>{selectedPath}</span>
            </span>
            <span className={cn.selectedPathActions}>
              <AtomButton
                variant="secondary"
                size="small"
                icon={<AimOutlined aria-hidden />}
                onClick={handleShowSelected}
              >
                Показать в списке
              </AtomButton>
              <AtomButton
                variant="secondary"
                size="small"
                icon={<CloseOutlined aria-hidden />}
                onClick={handleClearSelection}
              >
                Отменить выбор
              </AtomButton>
            </span>
          </div>
        )}
        {errorMessage && (
          <span role="alert" className={cn.error}>
            <ExclamationCircleFilled aria-hidden />
            {errorMessage}
          </span>
        )}
      </div>

      <TreeEntityCreationModal
        key={selectedId ?? 'none'}
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        treeType={treeType}
        externalTree={tree}
        setExternalTree={setExternalTree}
        defaultParentId={selectedId}
        onSuccess={handleCreated}
      />
    </div>
  )
}

export default HierarchyTreeColumn
